
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { 
  LayoutDashboard, Users, Trello, FileText, LogOut, Menu, ClipboardList, Map as MapIcon, Loader2, Globe, History as HistoryIcon, Plus, Shield, Bell, Moon, Sun, ChevronDown, Lock, CreditCard, AlertOctagon, HelpCircle, WifiOff, RefreshCw, Eye, EyeOff, Save, CheckCircle2, X, MessageSquare, ChevronRight, Cake, Settings2, Send
} from 'lucide-react';
import { supabase } from './supabase';
import { defaultContactCategories } from './data';
import { UserRole, Task, Document, Contact, Attendance, User, Container, TaskStatus, TaskStatus as TaskStatusEnum, TaskSector, DEFAULT_TASK_SECTORS, Institution, AuditEvent, Notification, SessionLog, Attachment, CustomSector, WhatsAppAssessor } from './types';
import Dashboard from './components/Dashboard';
import KanbanBoard from './components/KanbanBoard';
import CRMList from './components/CRMList';
import CreateModal from './components/CreateModal';
import Login from './components/Login';
import Register from './components/Register';
import MapModule from './components/MapModule';
import HistoryLog from './components/HistoryLog';
import AdminSettings from './components/AdminSettings';
import DocumentList from './components/OfficialLetterList'; 
import AttendanceList from './components/AttendanceList';
import SuperAdminDashboard from './components/SuperAdminDashboard';
import NotificationCenter from './components/NotificationCenter';
import BirthdayModal from './components/BirthdayModal'; 
import LandingPage from './components/LandingPage';
import Onboarding from './components/Onboarding';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfUse from './components/TermsOfUse';
import SupportModal from './components/SupportModal';
import PublicAttendanceForm from './components/PublicAttendanceForm';
import WhatsAppPanel from './components/WhatsAppPanel';

type ModuleId = 'dashboard' | 'crm' | 'kanban' | 'attendance' | 'documents' | 'history' | 'map' | 'settings' | 'superadmin' | 'whatsapp' | 'whatsapp-groups' | 'whatsapp-broadcasts' | 'whatsapp-settings' | 'whatsapp-birthdays' | 'whatsapp-flows' | 'login' | 'landing' | 'terms' | 'privacy' | 'public-request';

const SLUG_MAP: Record<ModuleId, string> = {
  dashboard: 'inicio',
  crm: 'contatos',
  kanban: 'tarefas',
  attendance: 'atendimentos',
  documents: 'documentos',
  history: 'historico',
  map: 'mapa',
  settings: 'ajustes',
  superadmin: 'admin-global',
  whatsapp: 'whatsapp',
  'whatsapp-groups': 'whatsapp-grupos',
  'whatsapp-broadcasts': 'whatsapp-transmissao',
  'whatsapp-settings': 'whatsapp-ajustes',
  'whatsapp-birthdays': 'whatsapp-aniversarios',
  'whatsapp-flows': 'whatsapp-disparos',
  login: 'login',
  landing: 'landing',
  terms: 'termos',
  privacy: 'privacidade',
  'public-request': 'solicitacao'
};

const REVERSE_SLUG_MAP: Record<string, ModuleId> = Object.entries(SLUG_MAP).reduce((acc, [k, v]) => ({ ...acc, [v]: k as ModuleId }), {} as Record<string, ModuleId>);

const parsePath = (pathname: string) => {
  if (pathname === '/login') return { tab: 'login' as ModuleId, containerSlug: null };
  if (pathname === '/' || pathname === '') return { tab: 'landing' as ModuleId, containerSlug: null };
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length === 0) return { tab: 'landing' as ModuleId, containerSlug: null };
  const potentialTab = REVERSE_SLUG_MAP[parts[0]];
  if (potentialTab) return { tab: potentialTab, containerSlug: null };
  const containerSlug = parts[0];
  const tabSlug = parts[1];
  const tab = tabSlug ? REVERSE_SLUG_MAP[tabSlug] : 'dashboard';
  return { tab: tab as ModuleId, containerSlug };
};

const App: React.FC = () => {
  const initializedUserIdRef = useRef<string | null>(null);

  const [activeTab, setActiveTab] = useState<ModuleId>(() => {
    if (typeof window === 'undefined') return 'dashboard';
    return parsePath(window.location.pathname).tab;
  });
  const [publicRequestSlug] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    return parsePath(window.location.pathname).containerSlug;
  });
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [createModalType, setCreateModalType] = useState<'task' | 'contact' | 'document' | 'attendance' | null>(null);
  const [initialTaskStatus, setInitialTaskStatus] = useState<TaskStatus | null>(null);
  const [prefillContact, setPrefillContact] = useState<{ phone?: string; name?: string; photoUrl?: string } | undefined>();
  const [prefillContactId, setPrefillContactId] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const [authView, setAuthView] = useState<'login' | 'register'>('login');
  const [autoOpenProfile, setAutoOpenProfile] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => (typeof window !== 'undefined' ? localStorage.getItem('theme') === 'dark' : false));
  const [showNotifications, setShowNotifications] = useState(false);
  const [showCreateMenu, setShowCreateMenu] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);
  
  const [users, setUsers] = useState<User[]>([]);
  const [waAssessors, setWaAssessors] = useState<WhatsAppAssessor[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [attendances, setAttendances] = useState<Attendance[]>([]);
  const [institutions, setInstitutions] = useState<Institution[]>([]);
  const [auditLogs, setAuditLogs] = useState<AuditEvent[]>([]);
  const [allContainers, setAllContainers] = useState<Container[]>([]);
  const [currentContainer, setCurrentContainer] = useState<Container | null>(null);
  const [viewingContainerId, setViewingContainerId] = useState<string | null>(null);
  const [customSectors, setCustomSectors] = useState<CustomSector[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [session, setSession] = useState<any>(null);
  const [birthdayContactId, setBirthdayContactId] = useState<string | null>(null);
  const [whatsAppMounted, setWhatsAppMounted] = useState(false);

  const activeContainerId = useMemo(() => {
    if (currentUser?.role === UserRole.SUPERADMIN) {
      return viewingContainerId || currentUser?.container_id;
    }
    return currentUser?.container_id || null;
  }, [currentUser, viewingContainerId]);

  useEffect(() => {
    if (isDarkMode) { document.documentElement.classList.add('dark'); localStorage.setItem('theme', 'dark'); }
    else { document.documentElement.classList.remove('dark'); localStorage.setItem('theme', 'light'); }
  }, [isDarkMode]);

  const updateUrl = (tab: ModuleId, containerSlug?: string) => {
    const tabSlug = SLUG_MAP[tab] || 'inicio';
    const newPath = containerSlug ? `/${containerSlug}/${tabSlug}` : `/${tabSlug}`;
    try { if (window.location.pathname !== newPath) window.history.pushState({ tab, containerSlug }, '', newPath); } catch (e) {}
  };

  const fetchContainer = async (containerId: string | null) => {
    if (!containerId) { setCurrentContainer(null); return; }
    const { data } = await supabase.from('containers').select('*').eq('id', containerId).maybeSingle();
    if (data) setCurrentContainer(data);
  };

  const fetchCabinetData = async (containerId: string, userId?: string) => {
    if (!containerId || containerId === 'undefined' || containerId === 'null') return;
    try {
      // Contacts com paginação — Supabase retorna max 1000 rows por request por padrão.
      // Cabinetes Pro/Advanced podem ter mais de 1000 contatos, então buscamos em páginas.
      const fetchAllContacts = async () => {
        const PAGE_SIZE = 1000;
        const all: any[] = [];
        let page = 0;
        while (true) {
          const { data, error } = await supabase
            .from('contacts')
            .select('*')
            .eq('container_id', containerId)
            .is('deleted_at', null)
            .order('created_at', { ascending: false })
            .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);
          if (error) { console.error('[fetchCabinetData] contacts error (page', page, '):', error); break; }
          if (!data || data.length === 0) break;
          all.push(...data);
          if (data.length < PAGE_SIZE) break; // última página
          page++;
        }
        return all;
      };

      const [allContacts, tData, dData, aData, uData, sData, iData, nData, auditData] = await Promise.all([
        fetchAllContacts(),
        supabase.from('tasks').select('*').eq('container_id', containerId).is('deleted_at', null),
        supabase.from('documents').select('*').eq('container_id', containerId).is('deleted_at', null),
        supabase.from('attendances').select('*').eq('container_id', containerId).is('deleted_at', null),
        supabase.from('profiles').select('*').eq('container_id', containerId),
        supabase.from('task_sectors').select('*').eq('container_id', containerId),
        supabase.from('institutions').select('*').eq('container_id', containerId),
        supabase.from('notifications').select('*').eq('user_id', userId || currentUser?.id).order('timestamp', { ascending: false }).limit(50),
        supabase.from('audit_events').select('*').eq('container_id', containerId).order('timestamp', { ascending: false }).limit(1000)
      ]);
      setContacts(allContacts);
      if (tData.data) setTasks(tData.data);
      if (dData.data) setDocuments(dData.data);
      if (aData.data) setAttendances(aData.data);
      if (uData.data) setUsers(uData.data);
      const { data: assessorData } = await supabase.from('whatsapp_assessors').select('*').eq('container_id', containerId).eq('active', true);
      if (assessorData) setWaAssessors(assessorData);
      if (sData.data) setCustomSectors(sData.data);
      if (iData.data) setInstitutions(iData.data);
      if (nData.data) setNotifications(nData.data as any);
      
      if (auditData.data) {
        setAuditLogs(auditData.data as any);
        if (auditData.data.length > 0) {
            console.log(`[Audit] ${auditData.data.length} logs carregados.`);
        }
      }
    } catch (e) { console.error(e); }
  };

  const fetchUserProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase.from('profiles').select('*').eq('id', userId).maybeSingle();
      if (data) {
        setCurrentUser(data);
        const { tab, containerSlug } = parsePath(window.location.pathname);
        if (data.role === UserRole.SUPERADMIN) {
          const containersRes = await supabase.from('containers').select('*');
          if (containersRes.data) setAllContainers(containersRes.data);
          if (tab && tab !== 'login' && tab !== 'landing') setActiveTab(tab);
          else setActiveTab('superadmin');
        } 
        else if (data.container_id) {
          await fetchContainer(data.container_id);
          await fetchCabinetData(data.container_id, data.id);
          if (containerSlug && currentContainer && containerSlug !== currentContainer.slug) {
              updateUrl('dashboard', currentContainer.slug);
              setActiveTab('dashboard');
          } else {
              if (tab && tab !== 'login' && tab !== 'landing') setActiveTab(tab);
              else setActiveTab('dashboard');
          }
        } else {
          setActiveTab('dashboard'); 
        }
      } else {
        await supabase.auth.signOut();
      }
      setLoading(false);
    } catch (err) { setLoading(false); }
  };

  useEffect(() => {
    const init = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setSession(session);
        initializedUserIdRef.current = session.user.id;
        await fetchUserProfile(session.user.id);
      }
      else setLoading(false);
    };
    init();

    let profileSubscription: any = null;

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      if (event === 'SIGNED_IN' && session?.user) {
        // SIGNED_IN dispara também a cada renovação de token — só busca o perfil se for um novo login
        if (initializedUserIdRef.current !== session.user.id) {
          initializedUserIdRef.current = session.user.id;
          fetchUserProfile(session.user.id);
        }

        // Desconecta subscription anterior antes de criar nova (evita erro "after subscribe()")
        if (profileSubscription) {
          profileSubscription.unsubscribe();
        }

        profileSubscription = supabase
          .channel(`profile-${session.user.id}`)
          .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'profiles', filter: `id=eq.${session.user.id}` },
            (payload) => {
              if (payload.new.container_id !== payload.old.container_id) {
                window.location.reload();
              }
            }
          )
          .subscribe();
      }
      else if (event === 'SIGNED_OUT') {
        initializedUserIdRef.current = null;
        setCurrentUser(null);
        setCurrentContainer(null);
        setViewingContainerId(null);
        setActiveTab('landing');
        if (profileSubscription) profileSubscription.unsubscribe();
      }
    });

    return () => {
      subscription.unsubscribe();
      if (profileSubscription) profileSubscription.unsubscribe();
    };
  }, []);

  const logAction = async (type: string, name: string, description: string, entityId: string) => {
    const targetContainer = activeContainerId || currentUser?.container_id;
    if (!currentUser || !targetContainer) return;

    try {
        await supabase.from('audit_events').insert([{
            container_id: targetContainer, 
            user_id: currentUser.id, 
            action: description,
            entity_type: type, 
            entity_name: name, 
            entity_id: entityId, 
            timestamp: new Date().toISOString()
        }]);
        fetchCabinetData(targetContainer);
    } catch (e) {
        console.error("[Audit] Erro ao gravar log:", e);
    }
  };

  const isLeadership = useMemo(() => {
    return currentUser?.role?.toLowerCase() === 'leadership';
  }, [currentUser?.role]);

  const handleUpdateContact = async (id: string, updates: Partial<Contact>) => {
    const contact = contacts.find(c => c.id === id);
    if (contact) await logAction('CRM', contact.name, 'Atualizou informações do contato', id);
    const { error } = await supabase.from('contacts').update(updates).eq('id', id);
    if (!error) await fetchCabinetData(activeContainerId || '');
  };

  const handleDeleteContact = async (id: string) => {
    const item = contacts.find(c => c.id === id);
    if (!item) return false;

    // Se for leadership, só pode excluir se for o dono (quem criou)
    if (isLeadership && item.created_by !== currentUser?.id) {
        alert("Você só pode excluir contatos que você mesmo cadastrou.");
        return false;
    }

    await logAction('CRM', item.name, 'Moveu contato para lixeira', id);
    const { error } = await supabase.from('contacts').update({ deleted_at: new Date().toISOString() }).eq('id', id);
    if (error) return false;
    await fetchCabinetData(activeContainerId || '');
    return true;
  };

  const handleDeleteTask = async (id: string) => {
    if (isLeadership) return false;
    const item = tasks.find(t => t.id === id);
    if (!item) return false;
    await logAction('Tarefa', item.title, 'Moveu tarefa para lixeira', id);
    const { error } = await supabase.from('tasks').update({ deleted_at: new Date().toISOString() }).eq('id', id);
    if (!error) await fetchCabinetData(activeContainerId || '');
    return !error;
  };

  const visibleContacts = useMemo(() => {
    if (isLeadership && currentUser?.id) {
      return (contacts || []).filter(c => c.created_by === currentUser.id);
    }
    return contacts || [];
  }, [contacts, isLeadership, currentUser?.id]);

  const visibleAttendances = useMemo(() => {
    if (isLeadership && currentUser?.id) {
      return (attendances || []).filter(a => a.created_by === currentUser.id);
    }
    return attendances || [];
  }, [attendances, isLeadership, currentUser?.id]);

  const activeTasks = useMemo(() => {
    if (isLeadership) return [];
    return tasks || [];
  }, [tasks, isLeadership]);

  const activeDocuments = useMemo(() => {
    if (isLeadership) return [];
    return documents || [];
  }, [documents, isLeadership]);

  const taskSectors = useMemo(() => {
    const disabled = currentContainer?.disabled_sectors || [];
    const defaults = DEFAULT_TASK_SECTORS.filter(s => !disabled.includes(s));
    const customs = customSectors.filter(s => s.is_active).map(s => s.name);
    return [...new Set([...defaults, ...customs])];
  }, [customSectors, currentContainer]);

  const externalUnviewedCount = useMemo(() =>
    visibleAttendances.filter(a => a.source === 'public_form' && !a.viewed_at).length,
    [visibleAttendances]
  );

  const whatsappTabs: ModuleId[] = ['whatsapp', 'whatsapp-groups', 'whatsapp-broadcasts', 'whatsapp-settings', 'whatsapp-birthdays', 'whatsapp-flows'];
  const isWhatsAppTab = whatsappTabs.includes(activeTab);

  const hasWhatsAppAccess = useMemo(() => {
    return currentContainer?.plan === 'betatester' && (
      currentUser?.role === UserRole.ADMIN || currentUser?.role === UserRole.SUPERADMIN || !!currentUser?.permissions?.canAccessWhatsApp
    );
  }, [currentContainer?.plan, currentUser?.role, currentUser?.permissions?.canAccessWhatsApp]);

  useEffect(() => {
    if (isWhatsAppTab && hasWhatsAppAccess) setWhatsAppMounted(true);
  }, [isWhatsAppTab, hasWhatsAppAccess]);

  useEffect(() => {
    if (!hasWhatsAppAccess) setWhatsAppMounted(false);
  }, [hasWhatsAppAccess]);

  const menuItems = useMemo(() => {
    const items: { id: string; label: string; icon: any; badge?: number }[] = [];
    if (activeContainerId) {
        items.push({ id: 'dashboard', label: 'Início', icon: LayoutDashboard });
        items.push({ id: 'crm', label: 'Contatos', icon: Users });
        items.push({ id: 'attendance', label: 'Atendimentos', icon: ClipboardList, badge: externalUnviewedCount });
        items.push({ id: 'map', label: 'Mapa Estratégico', icon: MapIcon });
        if (!isLeadership) {
            items.splice(2, 0, { id: 'kanban', label: 'Tarefas', icon: Trello });
            items.splice(4, 0, { id: 'documents', label: 'Documentos', icon: FileText });
            items.splice(5, 0, { id: 'history', label: 'Histórico', icon: HistoryIcon });
        }
    }
    if (hasWhatsAppAccess) {
        items.push({ id: 'whatsapp', label: 'WhatsApp', icon: MessageSquare });
    }
    if (currentUser?.role === UserRole.SUPERADMIN) {
        items.push({ id: 'superadmin', label: 'Global Admin', icon: Shield });
    }
    return items;
  }, [currentUser, activeContainerId, isLeadership, externalUnviewedCount, currentContainer?.plan]);

  if (activeTab === 'public-request' && publicRequestSlug) {
    return <PublicAttendanceForm containerSlug={publicRequestSlug} />;
  }

  if (loading) return <div className="h-screen flex items-center justify-center dark:bg-navy-950 bg-white transition-colors"><Loader2 className="animate-spin text-parliament-600" size={48} /></div>;

  if (!session || !currentUser) {
    if (activeTab === 'login') {
      if (authView === 'register') return <Register onSwitchToLogin={() => setAuthView('login')} />;
      return <Login users={[]} onSwitchToRegister={() => setAuthView('register')} />;
    }
    if (activeTab === 'terms') return <TermsOfUse onBack={() => { setActiveTab('landing'); updateUrl('landing'); }} />;
    if (activeTab === 'privacy') return <PrivacyPolicy onBack={() => { setActiveTab('landing'); updateUrl('landing'); }} />;

    return (
      <>
        <LandingPage 
          onLoginClick={() => { setActiveTab('login'); setAuthView('login'); updateUrl('login'); }} 
          onNavigate={(p) => { setActiveTab(p as any); updateUrl(p as any); }} 
          onOpenSupport={() => setShowSupportModal(true)} 
        />
        {showSupportModal && <SupportModal currentUser={null} onClose={() => setShowSupportModal(false)} />}
      </>
    );
  }

  if (!currentUser.container_id && currentUser.role !== UserRole.SUPERADMIN) {
     return <Onboarding currentUser={currentUser} onRefresh={() => fetchUserProfile(currentUser.id)} />;
  }

  return (
    <div className="flex min-h-screen bg-white dark:bg-navy-950 transition-colors duration-300 font-sans">
      <aside className={`${isSidebarOpen ? 'w-64' : 'w-20'} bg-neutral-50 dark:bg-navy-900 border-r dark:border-navy-800 transition-all duration-300 flex flex-col h-screen sticky top-0 z-40 print:hidden`}>
        <div className="p-6 h-20 flex items-center gap-3 overflow-hidden shrink-0">
          <div className="bg-navy-900 p-2 rounded-lg text-white shadow-md"><Globe size={20} /></div>
          {isSidebarOpen && <h1 className="font-black text-lg tracking-tight uppercase dark:text-white">Gabinete</h1>}
        </div>
        <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
          {menuItems.map((item) => {
            const isWhatsApp = item.id === 'whatsapp';
            const isWhatsAppActive = isWhatsApp && isWhatsAppTab;
            const isActive = activeTab === item.id || isWhatsAppActive;

            return (
              <React.Fragment key={item.id}>
                <button onClick={() => { setActiveTab(item.id as any); updateUrl(item.id as any, currentContainer?.slug); }} className={`relative w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${isActive ? 'bg-navy-900 text-white shadow-lg' : 'text-neutral-500 dark:text-slate-400 hover:bg-neutral-200 dark:hover:bg-navy-800'}`}>
                  <span className="relative">
                    <item.icon size={20} />
                    {!!item.badge && item.badge > 0 && (
                      <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[8px] font-black rounded-full w-4 h-4 flex items-center justify-center">{item.badge > 9 ? '9+' : item.badge}</span>
                    )}
                  </span>
                  {isSidebarOpen && (
                    <>
                      <span className="text-sm font-bold tracking-tight flex items-center gap-2 flex-1">
                        {item.label}
                        {!!item.badge && item.badge > 0 && (
                          <span className="bg-red-500 text-white text-[9px] font-black rounded-full w-5 h-5 flex items-center justify-center">{item.badge > 9 ? '9+' : item.badge}</span>
                        )}
                      </span>
                      {isWhatsApp && <ChevronRight size={14} className={`transition-transform ${isWhatsAppActive ? 'rotate-90' : ''}`} />}
                    </>
                  )}
                </button>
                {isWhatsApp && isWhatsAppActive && isSidebarOpen && (
                  <div className="ml-9 space-y-0.5 mt-0.5 mb-1">
                    {([
                      { id: 'whatsapp', label: 'Conversas Individuais', icon: MessageSquare },
                      { id: 'whatsapp-groups', label: 'Gestão de Grupos', icon: Users },
                      { id: 'whatsapp-broadcasts', label: 'Listas de Transmissão', icon: Send },
                      { id: 'whatsapp-birthdays', label: 'Aniversários', icon: Cake },
                      { id: 'whatsapp-flows', label: 'Disparos', icon: Send },
                      { id: 'whatsapp-settings', label: 'Ajustes', icon: Settings2 },
                    ] as const).map(sub => (
                      <button key={sub.id} onClick={() => { setActiveTab(sub.id as any); updateUrl(sub.id as any, currentContainer?.slug); }}
                        className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-bold transition-all ${activeTab === sub.id ? 'text-white bg-navy-800' : 'text-slate-400 hover:text-white hover:bg-navy-800/50'}`}>
                        <sub.icon size={14} />
                        {sub.label}
                      </button>
                    ))}
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </nav>
        <div className="p-4 border-t dark:border-navy-800">
          <button onClick={() => { setActiveTab('settings'); setAutoOpenProfile(true); updateUrl('settings', currentContainer?.slug); }} className="w-full flex items-center gap-3 p-3 rounded-xl bg-neutral-100 dark:bg-navy-800 mb-4 hover:bg-neutral-200 transition-all">
            <div className="w-8 h-8 rounded-full bg-navy-900 text-white flex items-center justify-center font-bold text-xs">{currentUser.name?.charAt(0)}</div>
            {isSidebarOpen && <div className="text-left truncate flex-1"><p className="text-xs font-bold dark:text-white truncate">{currentUser.name}</p></div>}
          </button>
          <button onClick={async () => { await supabase.auth.signOut(); }} className="w-full flex items-center gap-3 px-4 py-3 text-red-600 rounded-xl hover:bg-red-50 transition-all"><LogOut size={20} />{isSidebarOpen && <span className="text-sm font-bold">Sair</span>}</button>
        </div>
      </aside>
      
      <main className="flex-1 flex flex-col min-w-0 h-screen overflow-hidden print:h-auto print:overflow-visible">
        <header className="h-20 border-b dark:border-navy-800 flex items-center justify-between px-8 shrink-0 bg-white dark:bg-navy-950 print:hidden">
          <div className="flex items-center gap-4">
            <button onClick={() => setIsSidebarOpen(!isSidebarOpen)} className="p-2 hover:bg-neutral-100 dark:hover:bg-navy-800 rounded-lg dark:text-white transition-colors"><Menu size={20} /></button>
            {viewingContainerId && (
              <span className="bg-parliament-50 text-parliament-700 px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest flex items-center gap-2">
                Visualizando: {currentContainer?.name}
                <button onClick={() => { setViewingContainerId(null); setActiveTab('superadmin'); updateUrl('superadmin'); }} className="hover:text-red-500"><X size={12}/></button>
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-3">
            {activeContainerId && (
              <div className="relative">
                <button onClick={() => setShowCreateMenu(!showCreateMenu)} className="bg-emerald-600 text-white px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg hover:opacity-90 transition-all flex items-center gap-2">NOVA AÇÃO <ChevronDown size={14} /></button>
                {showCreateMenu && (
                  <div className="absolute right-0 top-full mt-2 w-56 bg-white dark:bg-navy-900 border border-neutral-200 dark:border-navy-800 rounded-2xl shadow-2xl overflow-hidden z-50 animate-in slide-in-from-top-2">
                    <div className="p-2 space-y-1">
                        <button onClick={() => { setCreateModalType('contact'); setShowCreateMenu(false); }} className="w-full text-left px-4 py-3 hover:bg-neutral-50 dark:hover:bg-navy-800 rounded-xl flex items-center gap-3 transition-colors group"><div className="p-1.5 bg-navy-100 dark:bg-navy-800 text-navy-600 dark:text-white rounded-lg group-hover:bg-navy-900 group-hover:text-white transition-all"><Users size={16}/></div><span className="text-[10px] font-black text-navy-900 dark:text-white uppercase tracking-widest">Contato</span></button>
                        {!isLeadership && <button onClick={() => { setCreateModalType('task'); setShowCreateMenu(false); }} className="w-full text-left px-4 py-3 hover:bg-neutral-50 dark:hover:bg-navy-800 rounded-xl flex items-center gap-3 transition-colors group"><div className="p-1.5 bg-neutral-100 dark:bg-navy-800 text-neutral-600 dark:text-white rounded-lg group-hover:bg-navy-900 group-hover:text-white transition-all"><Trello size={16}/></div><span className="text-[10px] font-black text-navy-900 dark:text-white uppercase tracking-widest">Tarefa</span></button>}
                        <button onClick={() => { setCreateModalType('attendance'); setShowCreateMenu(false); }} className="w-full text-left px-4 py-3 hover:bg-neutral-50 dark:hover:bg-navy-800 rounded-xl flex items-center gap-3 transition-colors group"><div className="p-1.5 bg-emerald-100 dark:bg-emerald-900/20 text-emerald-600 rounded-lg group-hover:bg-emerald-600 group-hover:text-white transition-all"><ClipboardList size={16}/></div><span className="text-[10px] font-black text-navy-900 dark:text-white uppercase tracking-widest">Atendimento</span></button>
                        {!isLeadership && <button onClick={() => { setCreateModalType('document'); setShowCreateMenu(false); }} className="w-full text-left px-4 py-3 hover:bg-neutral-50 dark:hover:bg-navy-800 rounded-xl flex items-center gap-3 transition-colors group"><div className="p-1.5 bg-parliament-100 dark:bg-parliament-900/20 text-parliament-600 rounded-lg group-hover:bg-parliament-600 group-hover:text-white transition-all"><FileText size={16}/></div><span className="text-[10px] font-black text-navy-900 dark:text-white uppercase tracking-widest">Documento</span></button>}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </header>
        
        <div className="flex-1 px-8 py-8 overflow-y-auto custom-scrollbar print:overflow-visible print:p-0">
          {activeTab === 'dashboard' && activeContainerId && <Dashboard tasks={activeTasks} contacts={visibleContacts} letters={activeDocuments} attendances={visibleAttendances} users={users} setActiveTab={setActiveTab} currentUser={currentUser} isDarkMode={isDarkMode} onOpenBirthday={(c) => setBirthdayContactId(c.id)} container={currentContainer} auditLogs={auditLogs} />}
          {activeTab === 'crm' && activeContainerId && <CRMList contacts={visibleContacts} categories={defaultContactCategories} currentUser={currentUser} containerId={activeContainerId} onUpdateContact={handleUpdateContact} onDeleteContact={handleDeleteContact} onOpenCreate={() => setCreateModalType('contact')} onAddCategory={() => {}} onRemoveCategory={() => {}} onRefresh={() => fetchCabinetData(activeContainerId)} preselectedContactId={null} onClearPreselected={() => {}} onBulkDeleteContacts={async (ids) => { await supabase.from('contacts').update({deleted_at: new Date().toISOString()}).in('id', ids); fetchCabinetData(activeContainerId || ''); }} onBulkAddTags={() => Promise.resolve()} tasks={activeTasks} attendances={visibleAttendances} users={users} sectors={taskSectors} waAssessors={waAssessors} onRefreshData={() => fetchCabinetData(activeContainerId || '')} />}
          {activeTab === 'kanban' && activeContainerId && <KanbanBoard tasks={activeTasks} attendances={visibleAttendances} currentUser={currentUser} users={users} onUpdateTask={async (id, up) => { await supabase.from('tasks').update(up).eq('id', id); fetchCabinetData(activeContainerId || ''); }} onDeleteTask={handleDeleteTask} onUpdateAttendance={() => {}} onDeleteAttendance={() => {}} onOpenCreate={(s) => {setInitialTaskStatus(s); setCreateModalType('task');}} columnNames={{}} onUpdateColumnName={() => {}} contacts={visibleContacts} sectors={taskSectors} onAddSector={() => {}} onRemoveSector={() => {}} waAssessors={waAssessors} />}
          {activeTab === 'attendance' && activeContainerId && <AttendanceList attendances={visibleAttendances} users={users} contacts={visibleContacts} onUpdateAttendance={async (id, up) => { await supabase.from('attendances').update(up).eq('id', id); fetchCabinetData(activeContainerId || ''); }} onDeleteAttendance={async (id) => { await supabase.from('attendances').update({deleted_at: new Date().toISOString()}).eq('id', id); fetchCabinetData(activeContainerId || ''); }} onOpenCreate={() => setCreateModalType('attendance')} currentUser={currentUser} />}
          {activeTab === 'documents' && activeContainerId && <DocumentList letters={activeDocuments} users={users} contacts={visibleContacts} onUpdateLetter={async (id, up) => { await supabase.from('documents').update(up).eq('id', id); fetchCabinetData(activeContainerId || ''); }} onDeleteLetter={async (id) => { await supabase.from('documents').update({deleted_at: new Date().toISOString()}).eq('id', id); fetchCabinetData(activeContainerId || ''); }} onOpenCreate={() => setCreateModalType('document')} onViewContact={() => setActiveTab('crm')} currentUser={currentUser} containerId={activeContainerId} onRefresh={() => fetchCabinetData(activeContainerId || '')} />}
          {activeTab === 'history' && activeContainerId && <HistoryLog tasks={activeTasks} letters={activeDocuments} users={users} currentUser={currentUser} auditLogs={auditLogs} onRestore={() => {}} />}
          {activeTab === 'map' && activeContainerId && <MapModule contacts={visibleContacts} institutions={institutions} users={users} documents={activeDocuments} />}
          {activeTab === 'settings' && activeContainerId && <AdminSettings users={users} container={currentContainer} currentUser={currentUser} contacts={visibleContacts} autoOpenProfile={autoOpenProfile} onUpdateUsers={() => fetchCabinetData(activeContainerId || '')} sessionLogs={[]} onOpenSupport={() => setShowSupportModal(true)} waAssessors={waAssessors} onUpdateAssessors={() => fetchCabinetData(activeContainerId || '')} />}
          {activeTab === 'superadmin' && currentUser.role === UserRole.SUPERADMIN && <SuperAdminDashboard containers={allContainers} currentUser={currentUser} onUpdateContainers={async () => { const res = await supabase.from('containers').select('*'); if(res.data) setAllContainers(res.data); }} onViewContainer={(id) => { setViewingContainerId(id); fetchContainer(id); fetchCabinetData(id); setActiveTab('dashboard'); updateUrl('dashboard', allContainers.find(c => c.id === id)?.slug); }} />}
          {whatsAppMounted && activeContainerId && hasWhatsAppAccess && <div className={isWhatsAppTab ? '' : 'hidden'}><WhatsAppPanel subView={activeTab === 'whatsapp-settings' ? 'settings' : activeTab === 'whatsapp-birthdays' ? 'birthdays' : activeTab === 'whatsapp-flows' ? 'flows' : activeTab === 'whatsapp-groups' ? 'groups' : activeTab === 'whatsapp-broadcasts' ? 'broadcasts' : 'main'} currentUser={currentUser} container={currentContainer} contacts={visibleContacts} tasks={activeTasks} attendances={visibleAttendances} onCreateContact={(phone, name, photoUrl) => { setPrefillContact({ phone, name, photoUrl }); setCreateModalType('contact'); }} onViewContact={() => setActiveTab('crm')} onCreateForContact={(type, contactId) => { setPrefillContactId(contactId); setCreateModalType(type); }} onNavigate={(tab) => { setActiveTab(tab as any); updateUrl(tab as any, currentContainer?.slug); }} /></div>}

          {activeTab === 'terms' && <TermsOfUse onBack={() => { setActiveTab('dashboard'); updateUrl('dashboard', currentContainer?.slug); }} />}
          {activeTab === 'privacy' && <PrivacyPolicy onBack={() => { setActiveTab('dashboard'); updateUrl('dashboard', currentContainer?.slug); }} />}
        </div>
      </main>

      {createModalType && <CreateModal type={createModalType} initialTaskStatus={initialTaskStatus || undefined} prefillContact={createModalType === 'contact' ? prefillContact : undefined} prefillContactId={prefillContactId} onClose={() => { setCreateModalType(null); setPrefillContact(undefined); setPrefillContactId(undefined); }} onRefresh={() => fetchCabinetData(activeContainerId || '')} onCreateContact={() => {}} categories={defaultContactCategories} currentUser={currentUser} containerId={activeContainerId || undefined} users={users} contacts={visibleContacts} sectors={taskSectors} waAssessors={waAssessors} onLogAction={logAction} />}
      {birthdayContactId && <BirthdayModal contact={visibleContacts.find(c => c.id === birthdayContactId)!} currentUser={currentUser} onClose={() => setBirthdayContactId(null)} />}
      {showSupportModal && <SupportModal currentUser={currentUser} onClose={() => setShowSupportModal(false)} containerId={activeContainerId} />}
    </div>
  );
};

export default App;
